import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { prefetchRoute } from '@/lib/prefetch';
import {
  TrendingUp,
  PieChart,
  ArrowLeftRight,
  ScrollText,
  Globe,
  Bitcoin,
  Building2,
  FileText,
  FileSearch,
  Database,
  Newspaper,
  Leaf,
  Award,
  Share2,
  MessageCircle,
  Activity
} from 'lucide-react';

const ProductsHoverContainer = styled.div`
  width: 60vw;
  max-width: 1000px;
  min-width: 900px;
  border-radius: 12px;
  box-shadow: 5px 6px 12px 2px rgba(var(--greyr), 0.3);
  padding: 16px;
  display: flex;
  gap: 0;
  background-color: var(--white);

  @media (max-width: 968px) {
    min-width: 0;
    width: calc(100% - 40px);
    flex-direction: column;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
    margin-bottom: 20px;
  }
`;

/* ── Left image panel ── */
const ImagePanel = styled(Link)`
  width: 200px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  display: block;
  text-decoration: none;
  margin-right: 24px;

  &:after {
    height: 0px !important;
    bottom: 0px !important;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 968px) {
    display: none!important;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.55) 100%);
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 6px;
`;

const ImageEyebrow = styled.span`
  font-size: 11px;
  color: rgba(255,255,255,0.75);
  font-weight: 500;
  letter-spacing: 0.3px;
`;

const ImageTitle = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.3;
`;

/* ── Columns ── */
const Columns = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 20px;
  border-left: 1px solid rgba(var(--greyr), 0.15);

  &:first-of-type {
    border-left: none;
    padding-left: 4px;
  }

  @media (max-width: 968px) {
    border-left: none;
    padding: 0;
  }
`;

const ColumnTitle = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: var(--grey);
  margin: 0 0 14px 0;
  letter-spacing: 0.2px;

  @media (max-width: 968px) {
    display: none;
  }
`;

/* ── Menu items ── */
const MenuItem = styled(Link)`
  display: flex !important;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 7px;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s;

  &:after {
    height: 0px !important;
    bottom: 0px !important;
  }

  &:hover {
    background-color: rgba(var(--greyr), 0.1);
  }
  @media (max-width: 968px) {
    padding-left: 10px!important;
  }
`;

const IconWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: rgba(var(--greyr), 0.1);
  border: 1px solid rgba(var(--greyr), 0.12);
  margin-top: 1px;

  svg {
    width: 15px;
    height: 15px;
    color: var(--grey);
  }
`;

const ItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--black);
  line-height: 1.3;
`;

const ItemDescription = styled.span`
  font-size: 12.5px;
  color: var(--grey);
  line-height: 1.4;
`;

const ProductsHover = () => {
  return (
    <ProductsHoverContainer>
      <ImagePanel to="/products" onMouseEnter={() => prefetchRoute('/products')}>
        <img
          src="/nav/3.png"
          alt="Featured"
          loading="lazy"
        />
        <ImageOverlay>
          <ImageEyebrow>Learn more about your data</ImageEyebrow>
          <ImageTitle>Unlock Your Financial Edge Today</ImageTitle>
        </ImageOverlay>
      </ImagePanel>

      <Columns>
        <Column>
          <ColumnTitle>Market</ColumnTitle>
          <MenuItem to="/products/stocks" onMouseEnter={() => prefetchRoute('/products/stocks')}>
            <IconWrapper><TrendingUp /></IconWrapper>
            <ItemText>
              <ItemTitle>Stocks</ItemTitle>
              <ItemDescription>Price data & market details</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/etfs" onMouseEnter={() => prefetchRoute('/products/etfs')}>
            <IconWrapper><PieChart /></IconWrapper>
            <ItemText>
              <ItemTitle>ETFs</ItemTitle>
              <ItemDescription>Holdings & constituent weights</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/forex" onMouseEnter={() => prefetchRoute('/products/forex')}>
            <IconWrapper><ArrowLeftRight /></IconWrapper>
            <ItemText>
              <ItemTitle>Forex</ItemTitle>
              <ItemDescription>Live currency pair rates</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/futures" onMouseEnter={() => prefetchRoute('/products/futures')}>
            <IconWrapper><ScrollText /></IconWrapper>
            <ItemText>
              <ItemTitle>Futures</ItemTitle>
              <ItemDescription>Contract specs & pricing</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/indices" onMouseEnter={() => prefetchRoute('/products/indices')}>
            <IconWrapper><Globe /></IconWrapper>
            <ItemText>
              <ItemTitle>Indices</ItemTitle>
              <ItemDescription>Global benchmarks & exposure</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/crypto" onMouseEnter={() => prefetchRoute('/products/crypto')}>
            <IconWrapper><Bitcoin /></IconWrapper>
            <ItemText>
              <ItemTitle>Crypto</ItemTitle>
              <ItemDescription>Digital asset price data</ItemDescription>
            </ItemText>
          </MenuItem>
        </Column>

        <Column>
          <ColumnTitle>Fundamental</ColumnTitle>
           <MenuItem to="/products/corporate-profiles" onMouseEnter={() => prefetchRoute('/products/corporate-profiles')}>
            <IconWrapper><Building2 /></IconWrapper>
            <ItemText>
              <ItemTitle>Profiles</ItemTitle>
              <ItemDescription>Company identity & structure</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/earnings" onMouseEnter={() => prefetchRoute('/products/earnings')}>
            <IconWrapper><FileText /></IconWrapper>
            <ItemText>
              <ItemTitle>Earnings</ItemTitle>
              <ItemDescription>Reports & analyst estimates</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/disclosures" onMouseEnter={() => prefetchRoute('/products/disclosures')}>
            <IconWrapper><FileSearch /></IconWrapper>
            <ItemText>
              <ItemTitle>Disclosures</ItemTitle>
              <ItemDescription>Standardized filings & financial metrics</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/economics" onMouseEnter={() => prefetchRoute('/products/economics')}>
            <IconWrapper><Database /></IconWrapper>
            <ItemText>
              <ItemTitle>Macros</ItemTitle>
              <ItemDescription>Broad economic datasets</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/news" onMouseEnter={() => prefetchRoute('/products/news')}>
            <IconWrapper><Newspaper /></IconWrapper>
            <ItemText>
              <ItemTitle>News</ItemTitle>
              <ItemDescription>Coverage by company & sector</ItemDescription>
            </ItemText>
          </MenuItem>
        </Column>

        <Column>
          <ColumnTitle>Alternative</ColumnTitle>
          <MenuItem to="/products/esg" onMouseEnter={() => prefetchRoute('/products/esg')}>
            <IconWrapper><Leaf /></IconWrapper>
            <ItemText>
              <ItemTitle>ESG</ItemTitle>
              <ItemDescription>Sustainability ratings</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/credit" onMouseEnter={() => prefetchRoute('/products/credit')}>
            <IconWrapper><Award /></IconWrapper>
            <ItemText>
              <ItemTitle>Credit</ItemTitle>
              <ItemDescription>Ratings across entity types</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/supply-chain" onMouseEnter={() => prefetchRoute('/products/supply-chain')}>
            <IconWrapper><Share2 /></IconWrapper>
            <ItemText>
              <ItemTitle>Supply Chain</ItemTitle>
              <ItemDescription>Peers, customers & rivals</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/sentiment" onMouseEnter={() => prefetchRoute('/products/sentiment')}>
            <IconWrapper><MessageCircle /></IconWrapper>
            <ItemText>
              <ItemTitle>Sentiment</ItemTitle>
              <ItemDescription>Aggregated market mood</ItemDescription>
            </ItemText>
          </MenuItem>
          <MenuItem to="/products/webtraffic" onMouseEnter={() => prefetchRoute('/products/webtraffic')}>
            <IconWrapper><Activity /></IconWrapper>
            <ItemText>
              <ItemTitle>Web Traffic</ItemTitle>
              <ItemDescription>Digital presence & engagement</ItemDescription>
            </ItemText>
          </MenuItem>

        </Column>
      </Columns>
    </ProductsHoverContainer>
  );
};

export default ProductsHover;
